import * as DSAssetFamilies from 'customer-data-filters-ui/filterQueryFormat/DSAssetFamilies/DSAssetFamilies';
import { CONTACT, COMPANY, DEAL, ORDER, CART, TICKET, PARTNER_CLIENT, PARTNER_ACCOUNT, SERVICE, COURSE, LISTING, APPOINTMENT } from 'customer-data-objects/constants/ObjectTypes';
import { hasPartnerAccountLists } from '../permissions/gates';
import { AppObjectTypeIdRegex, CustomObjectTypeIdRegex } from 'customer-data-filters-ui/filterQueryFormat/ObjectTypeId';
import { OrderedMap, Set as ImmutableSet } from 'immutable';
import { COMPANY_TYPE_ID, CONTACT_TYPE_ID, DEAL_TYPE_ID, TICKET_TYPE_ID, CALL_TYPE_ID, CART_TYPE_ID, ORDER_TYPE_ID, PARTNER_CLIENT_TYPE_ID, PARTNER_ACCOUNT_TYPE_ID, SERVICE_TYPE_ID, COURSE_TYPE_ID, LISTING_TYPE_ID, APPOINTMENT_TYPE_ID } from 'customer-data-objects/constants/ObjectTypeIds';
import getIn from 'transmute/getIn';
import memoize from 'transmute/memoize';
import AssociatedCompanyProperty from 'customer-data-objects/property/special/AssociatedCompanyProperty';
import AssociatedContactProperty from 'customer-data-objects/property/special/AssociatedContactProperty';
import InboundDbImportProperty from 'customer-data-objects/property/special/InboundDbImportProperty';
import InboundDbListMembershipProperty from '../converters/crmSearch/psuedoProperties/InboundDbListMembershipProperty';
import FormSubmissionsProperty from 'customer-data-objects/property/special/FormSubmissionsProperty';
import ListMembershipsProperty from 'customer-data-objects/property/special/ListMembershipsProperty';
import DealStageProbabilityProperty from 'customer-data-objects/property/special/DealStageProbabilityProperty';
import PropertyRecordToDSFieldRecord from 'customer-data-filters-ui/filterQueryFormat/DSFieldRecord/formatter/PropertyRecordToDSFieldRecord';
import PropertyRecord from 'customer-data-objects/property/PropertyRecord';
import { ENUMERATION } from 'customer-data-objects/property/PropertyTypes';
import { getObjectName } from 'customer-data-filters-ui/strategy/filterFamilyEntityNameGetters';
import I18n from 'I18n';
import { categoryToType } from 'customer-data-filters-ui/filterQueryFormat/associations/AssociationUtilities';
const HiddenFilterProperties = ImmutableSet.of('dealstage.probability', 'hs_email_html');
const VisibleFilterProperties = ImmutableSet.of('associatedcompanyid', 'associations.company', 'associations.contact', 'formSubmissions.formId');
export function isVisibleFilterProperty(objectType, property, hasScope) {
  // if its a missing field, it should be shown
  if (!property) {
    return true;
  }
  const {
    hidden,
    name
  } = property;
  const hasILSLists = [CONTACT, CONTACT_TYPE_ID, COMPANY, COMPANY_TYPE_ID, DEAL, DEAL_TYPE_ID, CART, CART_TYPE_ID, ORDER, ORDER_TYPE_ID, TICKET, TICKET_TYPE_ID, PARTNER_CLIENT, PARTNER_CLIENT_TYPE_ID, PARTNER_ACCOUNT, PARTNER_ACCOUNT_TYPE_ID, SERVICE, SERVICE_TYPE_ID, COURSE, COURSE_TYPE_ID, LISTING, LISTING_TYPE_ID, APPOINTMENT, APPOINTMENT_TYPE_ID].includes(objectType) || CustomObjectTypeIdRegex.test(objectType) || AppObjectTypeIdRegex.test(objectType);
  if (name === InboundDbListMembershipProperty.name) {
    return hasILSLists && hasScope('ils-lists-read');
  }
  if (name === 'listMemberships.listId') {
    return !hasILSLists && hasScope('contacts-lists-read');
  }
  switch (name) {
    case '_inbounddbio.importid_':
      return hasScope('crm-import-read');
    default:
      return VisibleFilterProperties.contains(name) || !hidden && !HiddenFilterProperties.contains(name);
  }
}

// These groupnames MUST match the internal names of the groups on each object type
// for these magic properties to appear in the correct place.
const groupNames = {
  [CONTACT_TYPE_ID]: 'contactinformation',
  [COMPANY_TYPE_ID]: 'companyinformation',
  [DEAL_TYPE_ID]: 'dealinformation',
  [TICKET_TYPE_ID]: 'ticketinformation',
  [CALL_TYPE_ID]: 'callinformation',
  [PARTNER_CLIENT_TYPE_ID]: 'partner_client_information',
  [PARTNER_ACCOUNT_TYPE_ID]: 'partner_account_information'
};
export const setPropertyGroupName = (property, objectTypeId, propertyGroupNames = []) => {
  const fallbackPropertyGroupName = propertyGroupNames.find(propertyGroupName => propertyGroupName.endsWith('information'));
  return property.set('groupName', groupNames[objectTypeId] || fallbackPropertyGroupName);
};
export const getAssociationFields = (objectTypeId, objectDefinitions, associations) => {
  if (!associations) {
    return OrderedMap();
  }
  const fields = [];
  associations.forEach(associationsForType => {
    associationsForType.forEach(singleAssociation => {
      const {
        category,
        id,
        toObjectTypeId
      } = singleAssociation;
      const toName = getObjectName({
        objectTypeId: toObjectTypeId,
        objectDefinitions
      }) || toObjectTypeId;
      const propertyRecord = PropertyRecord({
        groupName: 'contactinformation',
        label: I18n.text('customerDataFiltersUI.fieldNames.genericAssociationLabel', {
          objectName: toName
        }),
        // @ts-expect-error ts-migrate(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
        name: `associations.${categoryToType[category]}-${id}`,
        type: ENUMERATION,
        referencedObjectType: toObjectTypeId
      });
      const propertyRecordWithGroupName = setPropertyGroupName(propertyRecord, objectTypeId);
      fields.push(propertyRecordWithGroupName);
    });
  });
  return PropertyRecordToDSFieldRecord(objectTypeId, fields);
};
const getAssociatedRecordProperty = (objectTypeId, propertyGroupNames) => {
  const AssociatedRecordProperty = PropertyRecord({
    label: I18n.text('customerDataFiltersUI.fieldNames.associationVirtualProperty'),
    name: 'associations.labeled',
    type: ENUMERATION
  });
  return setPropertyGroupName(AssociatedRecordProperty, objectTypeId, propertyGroupNames);
};

// HACK: These are psuedo-properties (magic) that are accepted by crmsearch but are not real properties.
// They are used to filter by things like list membership and associated objects, but cannot be used
// as table columns. This is enforced by GridProperties.
export const getMagicFieldsForObjectType = memoize((objectTypeId, propertyGroupNames, getIsUngated) => {
  const inboundDbImportProperty = setPropertyGroupName(InboundDbImportProperty, objectTypeId, propertyGroupNames);
  const inboundDbListMembershipProperty = setPropertyGroupName(InboundDbListMembershipProperty, objectTypeId, propertyGroupNames);
  const associatedCompanyProperty = setPropertyGroupName(AssociatedCompanyProperty, objectTypeId);
  const associatedContactProperty = setPropertyGroupName(AssociatedContactProperty, objectTypeId);
  const associatedRecordProperty = getAssociatedRecordProperty(objectTypeId, propertyGroupNames);
  if (CustomObjectTypeIdRegex.test(objectTypeId)) {
    return PropertyRecordToDSFieldRecord(objectTypeId, [associatedRecordProperty, inboundDbImportProperty, inboundDbListMembershipProperty]);
  }
  if (AppObjectTypeIdRegex.test(objectTypeId)) {
    return PropertyRecordToDSFieldRecord(objectTypeId, [
    // TODO: Associations aren't yet supported for app objects. This can be added when they are.
    // See https://git.hubteam.com/orgs/HubSpot/projects/672/views/1?pane=issue&itemId=47167 and linked doc
    // associatedRecordProperty,
    inboundDbImportProperty, inboundDbListMembershipProperty]);
  }
  switch (objectTypeId) {
    case CONTACT_TYPE_ID:
      {
        const formSubmissionsProperty = setPropertyGroupName(FormSubmissionsProperty, objectTypeId);
        const listMembershipsProperty = setPropertyGroupName(ListMembershipsProperty, objectTypeId);
        return PropertyRecordToDSFieldRecord(CONTACT_TYPE_ID, [associatedRecordProperty, inboundDbImportProperty, inboundDbListMembershipProperty, formSubmissionsProperty, listMembershipsProperty]);
      }
    case COMPANY_TYPE_ID:
      {
        return PropertyRecordToDSFieldRecord(COMPANY_TYPE_ID, [associatedRecordProperty, inboundDbImportProperty, inboundDbListMembershipProperty]);
      }
    case DEAL_TYPE_ID:
      {
        const dealStageProbabilityProperty = setPropertyGroupName(DealStageProbabilityProperty, objectTypeId);
        return PropertyRecordToDSFieldRecord(DEAL_TYPE_ID, [associatedRecordProperty, associatedCompanyProperty, associatedContactProperty, inboundDbImportProperty, ...(getIsUngated('CRM:Segments:DealListsCreation') ? [inboundDbListMembershipProperty] : []), dealStageProbabilityProperty]);
      }
    case TICKET_TYPE_ID:
      {
        return PropertyRecordToDSFieldRecord(TICKET_TYPE_ID, [associatedRecordProperty, associatedCompanyProperty, associatedContactProperty, inboundDbImportProperty, inboundDbListMembershipProperty]);
      }
    case CALL_TYPE_ID:
      {
        return PropertyRecordToDSFieldRecord(CALL_TYPE_ID, [inboundDbImportProperty, associatedRecordProperty]);
      }
    case CART_TYPE_ID:
      {
        return PropertyRecordToDSFieldRecord(CART_TYPE_ID, [associatedRecordProperty, inboundDbListMembershipProperty]);
      }
    case ORDER_TYPE_ID:
      {
        return PropertyRecordToDSFieldRecord(ORDER_TYPE_ID, [associatedRecordProperty, inboundDbListMembershipProperty]);
      }
    case PARTNER_CLIENT_TYPE_ID:
      {
        return PropertyRecordToDSFieldRecord(PARTNER_CLIENT_TYPE_ID, [associatedRecordProperty, inboundDbListMembershipProperty]);
      }
    case PARTNER_ACCOUNT_TYPE_ID:
      {
        return PropertyRecordToDSFieldRecord(PARTNER_ACCOUNT_TYPE_ID, [associatedRecordProperty, ...(hasPartnerAccountLists(getIsUngated) ? [inboundDbListMembershipProperty] : [])]);
      }
    case SERVICE_TYPE_ID:
      {
        return PropertyRecordToDSFieldRecord(SERVICE_TYPE_ID, [associatedRecordProperty, inboundDbListMembershipProperty]);
      }
    case COURSE_TYPE_ID:
      {
        return PropertyRecordToDSFieldRecord(COURSE_TYPE_ID, [associatedRecordProperty, inboundDbListMembershipProperty]);
      }
    case LISTING_TYPE_ID:
      {
        return PropertyRecordToDSFieldRecord(LISTING_TYPE_ID, [associatedRecordProperty, inboundDbListMembershipProperty]);
      }
    case APPOINTMENT_TYPE_ID:
      {
        return PropertyRecordToDSFieldRecord(APPOINTMENT_TYPE_ID, [associatedRecordProperty, inboundDbListMembershipProperty]);
      }
    default:
      {
        return PropertyRecordToDSFieldRecord(objectTypeId, [associatedRecordProperty]);
      }
  }
});
export const getFilteredFieldsWithMagicFields = (objectTypeId, data, includeAssociations, hasScope, getIsUngated) => {
  const fields = data.get(objectTypeId) || OrderedMap({});
  const propertyGroups = getIn([DSAssetFamilies.PROPERTY_GROUP, objectTypeId], data) || OrderedMap();
  const propertyGroupNames = Object.keys(propertyGroups.sort((a, b) => b.displayOrder - a.displayOrder).toJS());
  const magicFields = getMagicFieldsForObjectType(objectTypeId, propertyGroupNames, getIsUngated);
  let associationFields;
  if (includeAssociations) {
    const objectDefinitions = data.get(DSAssetFamilies.STANDARD_OBJECT_DEFINITION);
    const associations = data.getIn([DSAssetFamilies.ASSOCIATION_DEFINITION, objectTypeId]);
    associationFields = getAssociationFields(objectTypeId, objectDefinitions, associations);
  }
  return fields.merge(magicFields).merge(associationFields).filter(field => {
    return isVisibleFilterProperty(objectTypeId, field.wrappedObject, hasScope);
  });
};

// Strategies must support the PropertyFilter component,
//   which is used to edit a single filter condition.
// Ideally, strategies would implement a serializer for a single filter condition,
//   but the current architecture makes that difficult:
//   https://git.hubteam.com/HubSpot/customer-data-filters/pull/5235#discussion_r3865681
// These functions wrap/unwrap a single filter condition from a filter query.
export const wrapSingleCRMFilter = (singleFilter, objectTypeId) => ({
  objectTypeId,
  filterGroups: [{
    filters: singleFilter ? [singleFilter] : []
  }]
});
export const unwrapSingleCRMFilter = filterQuery => {
  var _filterQuery$filterGr;
  return filterQuery === null || filterQuery === void 0 || (_filterQuery$filterGr = filterQuery.filterGroups) === null || _filterQuery$filterGr === void 0 || (_filterQuery$filterGr = _filterQuery$filterGr[0]) === null || _filterQuery$filterGr === void 0 || (_filterQuery$filterGr = _filterQuery$filterGr.filters) === null || _filterQuery$filterGr === void 0 ? void 0 : _filterQuery$filterGr[0];
};